import { useCallback, useEffect, useState } from 'react'
import { useGlobalState } from 'context/GlobalStateContext'
import { usePayItemSummaryQuery } from 'schema'
import { createStrictContext } from 'utils/createContext'
import { DefaultQueryHookOptions } from 'utils/defaults'
import { Group, Internal, Item, PayItemsModuleContext } from './types'
import { InternalPayItemConnectionClassType } from 'shared'
import { sortByOrder } from './utils'

const { Provider, useContextFn } = createStrictContext<PayItemsModuleContext>(
  'PayItemsModuleContext'
)

export const usePayItemsModuleContext = useContextFn

const PayItemsModuleProvider: React.FC = ({ children }) => {
  const {
    state: { projectGlobalId: projectId },
  } = useGlobalState()
  const {
    data,
    loading: fetching,
    refetch,
  } = usePayItemSummaryQuery({
    ...DefaultQueryHookOptions,
    skip: !projectId,
    variables: { projectId: projectId! },
  })
  /**
   * we have to overwrite projectToInternalConnections because there's a chance
   * a new internal connection hasn't been configured yet
   */
  const [p2ic, setP2ic] = useState(Array<Internal>())
  useEffect(() => {
    setP2ic(
      InternalPayItemConnectionClassType.values.map((internal) => {
        const found = data?.project?.projectToInternalConnections?.find(
          ({ internalConnectionId }) => internalConnectionId === internal.id
        ) ?? {
          __typename: 'ProjectToInternalConnection',
          groupId: null,
          internalConnectionId: internal.id,
          order: null,
        }
        return { internal, ...found }
      })
    )
  }, [data?.project?.projectToInternalConnections])
  const internals = useCallback(
    (group?: Omit<Group, 'children'>) =>
      p2ic.reduce((internals, internal) => {
        if (group && internal.groupId === group.id) internals.push(internal)
        else if (!group && !internal.groupId) internals.push(internal)
        return internals
      }, Array<Internal>()),
    [p2ic]
  )
  const [sorted, setSorted] = useState(Array<Item>())
  useEffect(() => {
    setSorted(() =>
      [
        ...(data?.project?.payItemConnectionGroups ?? []).map<Group>(
          (group) => {
            const children = [
              ...(data?.project?.payItemConnections ?? []).filter(
                ({ groupId }) => groupId === group.id
              ),
              ...internals(group),
            ].sort(sortByOrder)
            return { ...group, children }
          }
        ),
        ...(data?.project?.payItemConnections ?? []).filter(
          ({ groupId }) => !groupId
        ),
        ...internals(),
      ].sort(sortByOrder)
    )
  }, [
    data?.project?.payItemConnectionGroups,
    data?.project?.payItemConnections,
    internals,
    projectId,
  ])
  return <Provider value={{ fetching, refetch, sorted }}>{children}</Provider>
}

export default PayItemsModuleProvider
